import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance, { publicRequest, clearLegacyAuthStorage } from './../../config/axios';
import { showToast } from './toastSlice';

clearLegacyAuthStorage();

export const register = createAsyncThunk(
    'auth/register',
    async ({ name, email, password, phone, recaptchaToken }, { rejectWithValue }) => {
        try {
            const url = '/auth/register';
            const response = await publicRequest({
                method: 'POST',
                url,
                data: { name, email, password, phone, recaptchaToken },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const verifyOTP = createAsyncThunk(
    'auth/verifyOTP',
    async ({ emailOTP, phoneOTP, email }, { rejectWithValue }) => {
        try {
            const url = '/auth/verify-otps';
            const response = await publicRequest({
                method: 'POST',
                url,
                data: { emailOTP, phoneOTP, email },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const resendOTP = createAsyncThunk(
    'auth/resendOTP',
    async ({ email }, { rejectWithValue }) => {
        try {
            const url = '/auth/resend-otp';
            const response = await publicRequest({
                method: 'POST',
                url,
                data: { email },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const login = createAsyncThunk(
    'auth/login',
    async (credentials, { rejectWithValue, dispatch }) => {
        try {
            const url = '/auth/login';
            const response = await publicRequest({
                method: 'POST',
                url,
                data: credentials,
            });
            dispatch(showToast({
                msg: `Hello! ${response.data.data.user.name}, Welcome back!`,
                state: 'success'
            }));
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchCurrentUser = createAsyncThunk(
    'auth/fetchCurrentUser',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance({
                method: 'GET',
                url: '/auth/me',
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const sendResetPasswordEmail = createAsyncThunk('auth/sendResetPasswordEmail', async ({ email, phone }, { rejectWithValue }) => {
    try {
        const url = '/auth/send-reset-password-otps';
        const response = await publicRequest({
            method: 'POST',
            url,
            data: email ? { email } : { phone },
        });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const resetPassword = createAsyncThunk('auth/resetPassword', async ({ email, phone, newPassword, emailOTP, phoneOTP }, { rejectWithValue }) => {
    try {
        const url = '/auth/reset-password';
        const response = await publicRequest({
            method: 'POST',
            url,
            data: { email, phone, newPassword, emailOTP, phoneOTP },
        });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const getUserSession = createAsyncThunk('auth/getUserSession', async (_, { rejectWithValue }) => {
    try {
        const url = '/auth/user-sessions';
        const response = await axiosInstance({
            method: 'GET',
            url,
        });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const revokeUserSession = createAsyncThunk('auth/revokeUserSession', async (sessionId, { rejectWithValue }) => {
    try {
        const url = `/auth/revoke-session/${sessionId}`;
        const response = await axiosInstance({
            method: 'DELETE',
            url,
        });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const terminateAllOtherSessions = createAsyncThunk('auth/terminateAllOtherSessions', async (_, { rejectWithValue }) => {
    try {
        const url = '/auth/terminate-other-sessions';
        const response = await axiosInstance({
            method: 'DELETE',
            url,
        });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const terminateSession = createAsyncThunk(
    'auth/terminateSession',
    async (sessionId, { rejectWithValue }) => {
        try {
            const url = `/auth/terminate-session/${sessionId}`;
            const response = await axiosInstance({
                method: 'DELETE',
                url,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const changePassword = createAsyncThunk('auth/changePassword', async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
        const url = '/auth/change-password';
        const response = await axiosInstance({
            method: 'POST',
            url,
            data: { currentPassword, newPassword },
        });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
    try {
        const url = '/auth/logout';
        await axiosInstance({
            method: 'POST',
            url,
        });
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

const initialState = {
    isAuthenticated: false,
    user: null,
    error: null,
    sessions: [],
    isLoading: false,
    authBootstrapped: false,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginSuccess: (state, action) => {
            state.isAuthenticated = true;
            state.user = action.payload;
            state.error = null;
        },
        loginFailure: (state, action) => {
            state.isAuthenticated = false;
            state.user = null;
            state.error = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        logoutImmediate: (state) => {
            state.isAuthenticated = false;
            state.user = null;
            state.error = null;
            state.sessions = [];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCurrentUser.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchCurrentUser.fulfilled, (state, action) => {
                state.isAuthenticated = true;
                state.user = action.payload.data.user;
                state.error = null;
                state.isLoading = false;
                state.authBootstrapped = true;
            })
            .addCase(fetchCurrentUser.rejected, (state) => {
                state.isAuthenticated = false;
                state.user = null;
                state.isLoading = false;
                state.authBootstrapped = true;
            })
            .addCase(register.fulfilled, (state) => {
                state.error = null;
                localStorage.setItem('kaxaa_signup_tour_pending', 'true');
                localStorage.setItem('kaxaa_welcome_bonus_pending', 'true');
            })
            .addCase(register.rejected, (state, action) => {
                state.isAuthenticated = false;
                state.user = null;
                state.error = action.payload?.error || action.payload;
            })
            .addCase(verifyOTP.fulfilled, (state, action) => {
                state.isAuthenticated = true;
                state.user = action.payload.data.user;
                state.authBootstrapped = true;
                localStorage.setItem('kaxaa_signup_tour_pending', 'true');
                localStorage.setItem('kaxaa_welcome_bonus_pending', 'true');
                state.error = null;
            })
            .addCase(verifyOTP.rejected, (state, action) => {
                state.isAuthenticated = false;
                state.user = null;
                state.error = action.payload?.error || action.payload;
            })
            .addCase(resendOTP.fulfilled, (state) => {
                state.error = null;
            })
            .addCase(resendOTP.rejected, (state, action) => {
                state.error = action.payload?.error || action.payload;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.isAuthenticated = true;
                state.user = action.payload.data.user;
                state.authBootstrapped = true;
                state.error = null;
            })
            .addCase(login.rejected, (state, action) => {
                state.isAuthenticated = false;
                state.user = null;
                state.error = action.payload?.error || action.payload;
            })
            .addCase(sendResetPasswordEmail.fulfilled, (state) => {
                state.error = null;
            })
            .addCase(sendResetPasswordEmail.rejected, (state, action) => {
                state.error = action.payload?.error || action.payload;
            })
            .addCase(resetPassword.fulfilled, (state) => {
                state.error = null;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.error = action.payload?.error || action.payload;
            })
            .addCase(getUserSession.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getUserSession.fulfilled, (state, action) => {
                state.sessions = action.payload?.data?.sessions || action.payload?.sessions || [];
                state.error = null;
                state.isLoading = false;
            })
            .addCase(getUserSession.rejected, (state, action) => {
                state.error = action.payload?.error || action.payload;
                state.isLoading = false;
            })
            .addCase(revokeUserSession.fulfilled, (state, action) => {
                if (state.sessions && Array.isArray(state.sessions)) {
                    state.sessions = state.sessions.filter(session =>
                        session.id !== action.payload?.sessionId &&
                        session._id !== action.payload?.sessionId
                    );
                }
                state.error = null;
            })
            .addCase(revokeUserSession.rejected, (state, action) => {
                state.error = action.payload?.error || action.payload;
            })
            .addCase(terminateAllOtherSessions.fulfilled, (state) => {
                if (state.sessions && Array.isArray(state.sessions)) {
                    state.sessions = state.sessions.filter(session => session.isCurrent);
                }
                state.error = null;
            })
            .addCase(terminateAllOtherSessions.rejected, (state, action) => {
                state.error = action.payload?.error || action.payload;
            })
            .addCase(terminateSession.fulfilled, (state, action) => {
                if (state.sessions && Array.isArray(state.sessions)) {
                    state.sessions = state.sessions.filter(
                        session =>
                            session.id !== action.meta.arg &&
                            session._id !== action.meta.arg
                    );
                }
                state.error = null;
            })
            .addCase(terminateSession.rejected, (state, action) => {
                state.error = action.payload?.error || action.payload || 'Failed to terminate session';
            })
            .addCase(logout.fulfilled, (state) => {
                state.isAuthenticated = false;
                state.user = null;
                state.error = null;
                state.authBootstrapped = true;
            })
            .addCase(logout.rejected, (state, action) => {
                state.error = action.payload?.error || action.payload;
            });
    },
});

export const performLogout = () => async (dispatch) => {
    try {
        await dispatch(logout()).unwrap();
    } catch (e) {
        console.error('Logout API missed or failed:', e);
    }

    clearLegacyAuthStorage();
    dispatch(logoutImmediate());
};

export const { loginSuccess, loginFailure, clearError, logoutImmediate } = authSlice.actions;

export default authSlice.reducer;
